import type {
  EnhancedScopeData,
  FieldValueScopeSource,
} from '@ardoq/data-model';
import type { FilterColorData, FilterColorEntry } from '@ardoq/scope-data';

let includedObjects: Record<string, boolean> = {};
let filterColorEntries: Record<string, FilterColorEntry> = {};
let warmedScopeDataBySource = new WeakMap<
  FieldValueScopeSource,
  EnhancedScopeData
>();

const isIncluded = (id: string): boolean | undefined => includedObjects[id];

const getColor = (id: string): string | undefined =>
  filterColorEntries[id]?.color || undefined;

const getFilterVersion = (id: string): number | undefined =>
  filterColorEntries[id]?.version;

const getFilterColorData = (): FilterColorData => ({
  entities: { ...filterColorEntries },
});

const set = (
  id: string,
  isIncluded: boolean,
  color: string,
  filterVersion: number | undefined
): void => {
  includedObjects[id] = isIncluded;

  if (color) {
    filterColorEntries[id] = { color, version: filterVersion };
  } else {
    // The entity might be matched by a filter which doesn't provide a color, in those
    // cases we want to make sure to remove any existing color formatting
    delete filterColorEntries[id];
  }
};

const unset = (id: string): void => {
  delete includedObjects[id];
  delete filterColorEntries[id];
};

const reset = (): void => {
  includedObjects = {};
  filterColorEntries = {};
  warmedScopeDataBySource = new WeakMap();
};

const isWarmedForScope = (
  scopeSource: FieldValueScopeSource,
  scopeData: EnhancedScopeData
): boolean => warmedScopeDataBySource.get(scopeSource) === scopeData;

const markWarmedForScope = (
  scopeSource: FieldValueScopeSource,
  scopeData: EnhancedScopeData
): void => {
  warmedScopeDataBySource.set(scopeSource, scopeData);
};

export const filterCache = {
  isIncluded,
  getColor,
  getFilterVersion,
  getFilterColorData,
  set,
  unset,
  reset,
  isWarmedForScope,
  markWarmedForScope,
};
